import React, { useState, useEffect, useMemo } from 'react';
import { RoleBadge } from './shared/RoleField';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import './CustomerPortal.css';

const now = new Date();
const DEFAULT_FROM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
const DEFAULT_TO   = now.toISOString().split('T')[0];

function CustomerPortal({ user, onLogout, hideHeader = false }) {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortByPriority, setSortByPriority] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  // View toggle
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  // Table view doesn't work on narrow screens -- force cards and hide the toggle so there's
  // no way to land in the bad table experience on a phone.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const effectiveViewMode = isMobile ? 'cards' : viewMode;

  // Funnel date range
  const [dateFrom, setDateFrom] = useState(DEFAULT_FROM);
  const [dateTo,   setDateTo]   = useState(DEFAULT_TO);

  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const truncateEmail = (email, maxLength = 25) => {
    if (!email) return '';
    return email.length <= maxLength ? email : email.substring(0, maxLength) + '...';
  };

  const fetchUserCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/my-companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching user companies:', err);
    }
  };

  useEffect(() => {
    fetchUserCompanies();
    fetchCandidates();
    const interval = setInterval(fetchCandidates, 30000);
    return () => clearInterval(interval);
  }, [selectedCompany]); // eslint-disable-line

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = selectedCompany ? `?companyId=${selectedCompany}` : '';
      const response = await axios.get(
        `${API_URL}/api/candidates/customer/${user.id}${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCandidates(response.data);
      setLoading(false);
    } catch (err) {
      console.error('[CustomerPortal] Error fetching candidates:', err);
      setLoading(false);
    }
  };

  // ── Funnel ────────────────────────────────────────────────────────────────
  const funnel = useMemo(() => {
    const from = dateFrom ? new Date(dateFrom) : null;
    const to   = dateTo   ? new Date(dateTo + 'T23:59:59') : null;

    const inRange = candidates.filter(c => {
      const d = new Date(c.created_at || c.date_added);
      if (from && d < from) return false;
      if (to   && d > to)   return false;
      return true;
    });

    const screened     = inRange.length;
    const fivePlus     = inRange.filter(c => (c.mountain_rating || 0) >= 5).length;
    const fadvSent     = inRange.filter(c => c.fadv_status?.toLowerCase() === 'sent').length;
    const sentToClient = inRange.filter(c => c.interview_sent_date).length;

    return {
      screened,
      fivePlus,
      fadvSent,
      sentToClient,
      pct5Plus : screened  > 0 ? Math.round(fivePlus     / screened  * 100) : 0,
      pctFadv  : fivePlus  > 0 ? Math.round(fadvSent     / fivePlus  * 100) : 0,
      pctSent  : fadvSent  > 0 ? Math.round(sentToClient / fadvSent  * 100) : 0,
    };
  }, [candidates, dateFrom, dateTo]);

  // ── Filtering & sorting ───────────────────────────────────────────────────
  const filteredCandidates = candidates.filter(c => {
    if (!searchTerm) return true;
    return c.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phone && c.phone.includes(searchTerm)) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const sortedCandidates = [...filteredCandidates].sort((a, b) => {
    if (sortByPriority && a.is_high_priority !== b.is_high_priority)
      return b.is_high_priority - a.is_high_priority;
    return new Date(b.date_added) - new Date(a.date_added);
  });

  const highPriorityCount = candidates.filter(c => c.is_high_priority).length;

  const getStatusColor = (status) => {
    if (!status) return '';
    const lower = status.toLowerCase();
    if (lower === 'ineligible' || lower.includes('fail')) return 'failed';
    if (lower.includes('complete') || lower.includes('pass') || lower.includes('eligible')) return 'complete';
    if (lower.includes('pending')) return 'pending';
    return '';
  };

  const resumeUrl = (c) => {
    if (c.mountain_resume_url) return `https://dashboard.mountainrecruiting.com/${c.mountain_resume_url}`;
    if (c.indeed_url) return c.indeed_url;
    return null;
  };

  const RatingBadge = ({ rating }) => {
    if (!rating) return null;
    const cls = rating >= 8 ? 'cp-rating high' : rating >= 5 ? 'cp-rating mid' : 'cp-rating low';
    return <span className={cls}>{rating}</span>;
  };

  // ── Card component ────────────────────────────────────────────────────────
  const CandidateCard = ({ candidate }) => {
    const url = resumeUrl(candidate);
    return (
    <div className="candidate-card">
      <div className="card-header">
        <h3>
          {url
            ? <a href={url} target="_blank" rel="noopener noreferrer" className="cp-name-link">{toTitleCase(candidate.candidate_name)}</a>
            : toTitleCase(candidate.candidate_name)}
          <RatingBadge rating={candidate.mountain_rating} />
          <RoleBadge role={candidate.job_role} />
        </h3>
        {candidate.is_high_priority && <span className="priority-badge">High Priority</span>}
      </div>
      <div className="card-body">
        <div className="info-section">
          <div className="info-row">
            <span className="label">Date Added:</span>
            <span className="value">{candidate.date_added}</span>
          </div>
          {candidate.csa_name && (
            <div className="info-row">
              <span className="label">CSA:</span>
              <span className="value">{candidate.csa_name}</span>
            </div>
          )}
          {candidate.phone && (
            <div className="info-row">
              <span className="label">Phone:</span>
              <span className="value">{candidate.phone}</span>
            </div>
          )}
          {candidate.email && (
            <div className="info-row">
              <span className="label">Email:</span>
              <span
                className="value"
                style={candidate.email?.includes('indeedemail.com') ? {color: 'red', fontWeight: 'bold'} : {}}
                title={candidate.email}
              >
                {truncateEmail(candidate.email)}
              </span>
            </div>
          )}
          <div className="info-row">
            <span className="label">Resume:</span>
            <span className="value">
              {candidate.indeed_url ? (
                <a href={candidate.indeed_url} target="_blank" rel="noopener noreferrer" style={{color: '#007bff'}}>View Resume</a>
              ) : (
                <span style={{color: '#6c757d', opacity: 0.6}}>Not Available</span>
              )}
            </span>
          </div>
        </div>
        <div className="cp-status-footer">
          <div className="cp-status-chip">
            <span className="cp-status-chip-label">BG</span>
            <span className={`cp-status-chip-val ${getStatusColor(candidate.bg_status)}`}>{candidate.bg_status || 'N/A'}</span>
          </div>
          <div className="cp-status-divider" />
          <div className="cp-status-chip">
            <span className="cp-status-chip-label">Drug</span>
            <span className={`cp-status-chip-val ${getStatusColor(candidate.drug_status)}`}>{candidate.drug_status || 'N/A'}</span>
          </div>
          <div className="cp-status-divider" />
          <div className="cp-status-chip">
            <span className="cp-status-chip-label">Physical</span>
            <span className={`cp-status-chip-val ${getStatusColor(candidate.physical_status)}`}>{candidate.physical_status || 'N/A'}</span>
          </div>
          <div className="cp-status-divider" />
          <div className="cp-status-chip">
            <span className="cp-status-chip-label">GCIC</span>
            <span className={`cp-status-chip-val ${getStatusColor(candidate.gcic_status)}`}>{candidate.gcic_status || 'N/A'}</span>
          </div>
        </div>
        {(candidate.last_worked || candidate.screening_status || candidate.fadv_status) && (
          <div className="additional-section">
            {candidate.last_worked && (
              <div className="info-row"><span className="label">Experience:</span><span className="value">{candidate.last_worked}</span></div>
            )}
            {candidate.screening_status && (
              <div className="info-row"><span className="label">Screening:</span><span className="value">{candidate.screening_status}</span></div>
            )}
            {candidate.fadv_status && (
              <div className="info-row"><span className="label">FADV:</span><span className="value">{candidate.fadv_status}</span></div>
            )}
          </div>
        )}
        {candidate.remarks && (
          <div className="remarks-section">
            <span className="label">Remarks:</span>
            <p className="remarks-text">{candidate.remarks}</p>
          </div>
        )}
      </div>
    </div>
    );
  };

  // ── Table view ────────────────────────────────────────────────────────────
  const TableView = () => (
    <div className="cp-table-wrap">
      <table className="cp-table">
        <thead>
          <tr>
            <th>DATE ADDED</th>
            <th>NAME</th>
            <th>CSA</th>
            <th>PHONE</th>
            <th>EMAIL</th>
            <th>BG</th>
            <th>DRUG</th>
            <th>PHYSICAL</th>
            <th>GCIC</th>
            <th>SCREENING</th>
            <th>FADV</th>
            <th>EXPERIENCE</th>
            <th>RESUME</th>
          </tr>
        </thead>
        <tbody>
          {sortedCandidates.map(c => {
            const url = resumeUrl(c);
            return (
              <tr key={c.id} className={c.is_high_priority ? 'cp-row-priority' : ''}>
                <td className="cp-td-date" data-label="Date Added">{c.date_added}</td>
                <td className="cp-td-name" data-label="Name">
                  {url
                    ? <a href={url} target="_blank" rel="noopener noreferrer" className="cp-name-link">{toTitleCase(c.candidate_name)}</a>
                    : toTitleCase(c.candidate_name)}
                  <RatingBadge rating={c.mountain_rating} />
                  <RoleBadge role={c.job_role} />
                  {c.is_high_priority && <span className="cp-priority-dot" title="High Priority" />}
                </td>
                <td data-label="CSA">{c.csa_name || '—'}</td>
                <td data-label="Phone">{c.phone || '—'}</td>
                <td data-label="Email" title={c.email}>{c.email ? truncateEmail(c.email, 22) : '—'}</td>
                <td className="cp-td-center" data-label="BG"><span className={`cp-status-badge ${getStatusColor(c.bg_status)}`}>{c.bg_status || 'N/A'}</span></td>
                <td className="cp-td-center" data-label="Drug"><span className={`cp-status-badge ${getStatusColor(c.drug_status)}`}>{c.drug_status || 'N/A'}</span></td>
                <td className="cp-td-center" data-label="Physical"><span className={`cp-status-badge ${getStatusColor(c.physical_status)}`}>{c.physical_status || 'N/A'}</span></td>
                <td className="cp-td-center" data-label="GCIC"><span className={`cp-status-badge ${getStatusColor(c.gcic_status)}`}>{c.gcic_status || 'N/A'}</span></td>
                <td className="cp-td-center" data-label="Screening">{c.screening_status || '—'}</td>
                <td className="cp-td-center" data-label="FADV">{c.fadv_status || '—'}</td>
                <td data-label="Experience">{c.last_worked || '—'}</td>
                <td className="cp-td-center" data-label="Resume">
                  {url
                    ? <a href={url} target="_blank" rel="noopener noreferrer" className="cp-resume-link">View</a>
                    : <span className="cp-no-resume">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="customer-portal">
        {!hideHeader && (
          <header className="app-header">
            <div className="header-left">
              <img src="/logo.png" alt="Mountain Recruiting" className="logo" />
              <h1>Driver Candidates</h1>
            </div>
            <div className="header-right">
              <span className="user-email">{user.email}</span>
              <button onClick={() => navigate('/client-interviews')}>Ready for Interview</button>
              <button onClick={onLogout} className="logout-btn">Logout</button>
            </div>
          </header>
        )}
        <div className="loading-state">Loading candidates...</div>
      </div>
    );
  }

  return (
    <div className="customer-portal">
      {!hideHeader && (
        <header className="app-header">
          <div className="header-left">
            <img src="/logo.png" alt="Mountain Recruiting" className="logo" />
            <h1>Driver Candidates</h1>
          </div>
          <div className="header-right">
            <span className="user-email">{user.email}</span>
            <button onClick={() => navigate('/client-interviews')}>Ready for Interview</button>
            <button onClick={() => navigate('/onboarding')}>Onboarding Information</button>
            <button onClick={onLogout} className="logout-btn">Logout</button>
          </div>
        </header>
      )}

      <div className="portal-content">

        {/* ── Top bar: heading + controls ── */}
        <div className="cp-top-bar">
          <h2 className="cp-page-title">Your Driver Candidates</h2>
          <div className="cp-header-actions">
            {/* Table isn't an option on mobile at all -- a lone "Cards" button would just be a
                functionless pill, so don't render the toggle rather than show dead UI. */}
            {!isMobile && (
            <div className="cp-view-toggle">
              <button
                className={`cp-toggle-btn ${effectiveViewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                ⊞ Cards
              </button>
              <button
                className={`cp-toggle-btn ${effectiveViewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                ≡ Table
              </button>
            </div>
            )}
            <button
              onClick={() => setSortByPriority(!sortByPriority)}
              className={`sort-btn ${sortByPriority ? 'active' : ''}`}
            >
              {sortByPriority ? '⭐ Priority View' : '📅 Date View'}
            </button>
          </div>
        </div>

        {/* ── Funnel ── */}
        <div className="cp-funnel-section">
          <div className="cp-funnel-controls">
            <span className="cp-funnel-label">Pipeline</span>
            <div className="cp-date-range">
              <input type="date" className="cp-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              <span className="cp-date-sep">→</span>
              <input type="date" className="cp-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>
          <div className="cp-funnel-bar">
            <div className="cp-funnel-cell dark">
              <div className="cp-funnel-metric-label">SCREENED</div>
              <div className="cp-funnel-value">{funnel.screened}</div>
            </div>
            <div className="cp-funnel-cell light">
              <div className="cp-funnel-arrow">→ 5+</div>
              <div className="cp-funnel-pct">{funnel.pct5Plus}%</div>
            </div>
            <div className="cp-funnel-cell dark">
              <div className="cp-funnel-metric-label">5+</div>
              <div className="cp-funnel-value">{funnel.fivePlus}</div>
            </div>
            <div className="cp-funnel-cell light">
              <div className="cp-funnel-arrow">→ FADV</div>
              <div className="cp-funnel-pct">{funnel.pctFadv}%</div>
            </div>
            <div className="cp-funnel-cell dark">
              <div className="cp-funnel-metric-label">FADV SENT</div>
              <div className="cp-funnel-value">{funnel.fadvSent}</div>
            </div>
            <div className="cp-funnel-cell light">
              <div className="cp-funnel-arrow">→ SENT</div>
              <div className="cp-funnel-pct">{funnel.pctSent}%</div>
            </div>
            <div className="cp-funnel-cell dark">
              <div className="cp-funnel-metric-label">SENT TO CLIENT</div>
              <div className="cp-funnel-value">{funnel.sentToClient}</div>
            </div>
          </div>
        </div>

        {/* ── Company filter ── */}
        {companies.length > 1 && (
          <div className="company-filter-section" style={{ marginBottom: '20px' }}>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Filter by Company:</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="company-filter"
              style={{ padding: '10px 15px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px', fontSize: '14px' }}
            >
              <option value="">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* ── Search ── */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {/* ── Candidates ── */}
        {sortedCandidates.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? 'No candidates match your search.' : 'No candidates yet. Your recruiter will add candidates here soon.'}
          </div>
        ) : effectiveViewMode === 'table' ? (
          <TableView />
        ) : (
          <div className="candidates-grid">
            {sortedCandidates.map(candidate => (
              <CandidateCard key={candidate.id} candidate={candidate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerPortal;
